import { ProductInventoryUnit } from '@gooduncles/gu-app-schema';
import { ColDef, ColGroupDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { FC, useMemo } from 'react';

import { formatNumberForAgCell, uomUnitCellStyle } from 'src/utils/ag-grid-util';
import { cellValidatorForInventoryUnit, dailyOutboundTransactionTableHandlers } from 'src/utils/product-inventory-util';
import { ProcessedOutboundTransaction } from 'src/utils/transaction-util';

const costEditorCellStyle = (params: any) =>
  cellValidatorForInventoryUnit(params) ? {} : { backgroundColor: 'var(--gray200)' };

const isRowSelectable = (params: any) => {
  const { ea, bundle, inventory } = params.data ?? {};
  return (
    // 신규 입력값이 있고 현재 매입가와 다른 경우
    (ea?.cost !== null && ea?.cost !== undefined && ea?.cost !== inventory?.uom.ea?.cost) ||
    (bundle?.cost !== null && bundle?.cost !== undefined && bundle?.cost !== inventory?.uom.bundle?.cost)
  );
};

interface DailyOutboundTransactionTableProps {
  date: string;
  rowData: ProcessedOutboundTransaction[];
  setSelectedRows: React.Dispatch<React.SetStateAction<ProcessedOutboundTransaction[]>>;
}

const defaultColDef: ColDef<ProcessedOutboundTransaction> = {
  sortable: true,
  resizable: true,
  filter: true,
};

const DailyOutboundTransactionTable: FC<DailyOutboundTransactionTableProps> = ({ date, rowData, setSelectedRows }) => {
  const columnDefs: (ColDef<ProcessedOutboundTransaction> | ColGroupDef<ProcessedOutboundTransaction>)[] = useMemo(
    () => [
      {
        headerName: '재고ID',
        field: 'inventoryId',
        hide: true,
      },
      {
        headerName: '',
        field: 'selection',
        width: 54,
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        checkboxSelection: true,
      },
      {
        headerName: '구분',
        field: 'productSupplier.groupId',
        width: 76,
      },
      {
        headerName: '재고명',
        field: 'inventory.inventoryName',
        width: 260,
        wrapText: true,
        autoHeight: true,
        cellStyle: (params) => ({
          lineHeight: '1.1em',
          display: 'flex',
          alignItems: 'center',
          letterSpacing: '-0.025em',
          wordBreak: 'keep-all',
          color: params.data?.isPreOrder ? '#ff1493' : 'inherit',
        }),
      },
      {
        headerName: '필요',
        field: 'totalQuantity',
        width: 100,
        filter: 'agNumberColumnFilter',
        cellStyle: { backgroundColor: '#ffff00' },
      },
      {
        headerName: '발주',
        field: 'purchaseQuantity',
        width: 100,
        filter: 'agNumberColumnFilter',
        cellStyle: (params) => {
          return params.value > 0 ? { backgroundColor: '#b6e1cd' } : null;
        },
      },
      {
        headerName: '재고',
        field: 'inventoryQuantity',
        width: 100,
        filter: 'agNumberColumnFilter',
        cellStyle: (params) => {
          return params.value > 0 ? { backgroundColor: '#d0e2f4' } : null;
        },
      },
      {
        headerName: '거래처',
        field: 'productSupplier.name',
        width: 130,
      },
      {
        headerName: '매입가 ea',
        children: [
          {
            headerName: '현재',
            field: 'inventory.uom.ea.cost',
            width: 100,
            type: 'numericColumn',
            valueFormatter: formatNumberForAgCell,
            cellStyle: uomUnitCellStyle,
          },
          {
            headerName: '*신규',
            field: 'ea.cost',
            width: 100,
            editable: true,
            type: 'numericColumn',
            cellStyle: costEditorCellStyle,
            valueFormatter: formatNumberForAgCell,
            valueSetter: dailyOutboundTransactionTableHandlers.cost.valueSetter,
            onCellValueChanged: (params) =>
              dailyOutboundTransactionTableHandlers.cost.onCellValueChanged(params, ProductInventoryUnit.EA, date),
          },
        ],
      },
      {
        headerName: '매입가 bundle',
        children: [
          {
            headerName: '현재',
            field: 'inventory.uom.bundle.cost',
            width: 100,
            type: 'numericColumn',
            valueFormatter: formatNumberForAgCell,
            cellStyle: uomUnitCellStyle,
          },
          {
            headerName: '*신규',
            field: 'bundle.cost',
            width: 100,
            editable: true,
            type: 'numericColumn',
            cellStyle: costEditorCellStyle,
            valueFormatter: formatNumberForAgCell,
            valueSetter: dailyOutboundTransactionTableHandlers.cost.valueSetter,
            onCellValueChanged: (params) =>
              dailyOutboundTransactionTableHandlers.cost.onCellValueChanged(params, ProductInventoryUnit.BUNDLE, date),
          },
        ],
      },
    ],
    [date]
  );
  return (
    <div className='ag-theme-alpine height100 bgWhite'>
      <AgGridReact
        rowData={rowData}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        alwaysShowHorizontalScroll={true}
        getRowId={(params) => params.data.inventoryId}
        onSelectionChanged={(e) => setSelectedRows(e.api.getSelectedRows())}
        // 행 선택
        rowSelection={'multiple'}
        isRowSelectable={isRowSelectable}
        // 셀 값이 변경된 후 해당 행이 선택 가능한지 확인하고, 선택 불가능하면 선택 해제
        onCellValueChanged={(params) => {
          const node = params.node;
          if (node.isSelected()) {
            const isSelectable = isRowSelectable(params);
            if (!isSelectable) {
              node.setSelected(false);
            }
          }
        }}
        // 행 선택을 켜놓더라도 개별 셀의 내용을 복사할 수 있도록한다.
        enableCellTextSelection={true}
      />
    </div>
  );
};

export default DailyOutboundTransactionTable;
